<script setup lang="ts">
import { callOnce, usePlaces } from "#imports";
import { watchDebounced } from "@vueuse/core";
import { computed, ref } from "vue";
import type { Place } from "~/stores/places";
import { appendComma } from "~/utils/text-formatting";

const props = withDefaults(
  defineProps<{
    name: string;
    placeholder?: string;
    withFilters?: boolean;
  }>(),
  {
    placeholder: "Where do you want to go",
  },
);

const modelValue = defineModel<Place | null>({ required: true });

const emits = defineEmits<{
  clickFilter: [];
}>();

const placesStore = usePlaces();

const isLoading = ref<boolean>(false);
const inputValue = ref<string>("");
const isSearch = ref<boolean>(false);

const load = async () => {
  isLoading.value = true;
  if (inputValue.value !== "") {
    await placesStore.search({ search: inputValue.value, limit: 20 });
    isSearch.value = true;
  } else {
    await placesStore.fetchAll({
      limit: 20,
      offset: 0,
      sortBy: "spot_count",
      sortDirection: "desc",
    });
    isSearch.value = false;
  }
  isLoading.value = false;
};

const results = computed(() => {
  if (isSearch.value) {
    return placesStore.searchResults!;
  } else {
    return placesStore.allPlaces!;
  }
});

await callOnce(load);

watchDebounced(inputValue, load, { debounce: 100 });

const selectPlace = (place: Place) => {
  modelValue.value = place;
  inputValue.value = "";
};
</script>

<template>
  <div id="place-search">
    <div v-if="props.withFilters" class="filter" @click="emits('clickFilter')">
      <i data-v-b4bc9cac="" class="ri-equalizer-2-line"></i>
    </div>
    <input
      type="text"
      :name="props.name"
      v-model="inputValue"
      :placeholder="
        modelValue
          ? appendComma(modelValue.place) +
            appendComma(modelValue.region) +
            modelValue.country
          : props.placeholder
      "
    />
    <div class="results typeface-regular">
      <div v-if="isLoading">Loading...</div>
      <div
        v-else
        v-for="place of results"
        :key="place.id"
        @click="selectPlace(place)"
        class="result"
      >
        {{ place.fullName }}
      </div>
      <div v-if="results.length === 0">Nothing found...</div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/input.pcss";
@import "@/assets/css/scrollbar.pcss";

#place-search {
  width: 100%;
  position: relative;
}

input {
  @mixin mp-input;
  width: 100%;
  padding: 1rem;
}

.filter {
  position: absolute;
  right: 0;
  top: 0.4rem;
  height: 2.5rem;
  border-left: 1px solid var(--color-slate-100);
  cursor: pointer;
  align-content: center;
  background: white;
  border-top-right-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;

  i {
    color: var(--color-slate-400);
    font-size: 1.5rem;
    padding: 0.5rem 1rem;
  }
}

input:focus + .results {
  visibility: visible;
  opacity: 1;
}

.results {
  transition:
    opacity 0.25s,
    visibility 0.25s;
  visibility: hidden;
  opacity: 0;
  position: absolute;
  z-index: 1;
  border: 1px OUTSET black;
  text-align: left;

  background: #fff;
  max-height: 15rem;
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
  width: 100%;
  border-radius: 0.5rem;
  color: var(--color-slate-500);

  > div {
    padding: 0.75rem 0.75rem 0.75rem 1rem;
    &.result:hover {
      cursor: pointer;
      background: lightgray;
    }
  }
}
</style>
